import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Badge, Button, Card, CardBody, Col, Row, Table } from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import { useAccountDashboard } from "hooks/useAccountDashboard"
import { useAccountExpenses, useAccountExpense } from "hooks/useAccountExpense"
import { useAccountFundTransfers, useAccountFundTransfer } from "hooks/useAccountFundTransfer"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"
import AccountExpenseAddEdit from "pages/AccountPages/AccountExpense/AccountExpenseAddEdit"
import AccountExpenseView from "pages/AccountPages/AccountExpense/AccountExpenseView"
import AccountFundTransferAddEdit from "pages/AccountPages/AccountFundTransfer/AccountFundTransferAddEdit"
import AccountFundTransferView from "pages/AccountPages/AccountFundTransfer/AccountFundTransferView"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import { getActiveRecords, getGrossAmount, getReversedAmount, getReversedRecords, getNetAmount } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "../components/WorkspaceStatCard"

const CashBankTab = ({ permissionFlags }) => {
  const [expenseModal, setExpenseModal] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState(null)
  const [expenseViewModal, setExpenseViewModal] = useState(false)
  const [selectedExpenseViewId, setSelectedExpenseViewId] = useState(null)
  const [depositModal, setDepositModal] = useState(false)
  const [selectedDeposit, setSelectedDeposit] = useState(null)
  const [depositViewModal, setDepositViewModal] = useState(false)
  const [selectedDepositViewId, setSelectedDepositViewId] = useState(null)

  const { data: dashboardResponse } = useAccountDashboard(permissionFlags.canViewAccounts)
  const { data: expenseResponse, isLoading: expenseLoading } = useAccountExpenses({ page: 1, per_page: 6 }, permissionFlags.canViewExpenses)
  const { data: depositResponse, isLoading: depositLoading } = useAccountFundTransfers({ page: 1, per_page: 6 }, permissionFlags.canViewFundTransfers)
  const { data: expenseViewResponse, isLoading: expenseViewLoading } = useAccountExpense(selectedExpenseViewId, expenseViewModal)
  const { data: depositViewResponse, isLoading: depositViewLoading } = useAccountFundTransfer(selectedDepositViewId, depositViewModal)

  const accountBalances = dashboardResponse?.data?.account_balances || []
  const expenses = expenseResponse?.data?.data || []
  const deposits = depositResponse?.data?.data || []

  const visibleExpenses = useMemo(() => getActiveRecords(expenses), [expenses])
  const visibleDeposits = useMemo(() => getActiveRecords(deposits), [deposits])
  const reversedExpenses = useMemo(() => getReversedRecords(expenses), [expenses])
  const reversedDeposits = useMemo(() => getReversedRecords(deposits), [deposits])

  const grossExpenseAmount = useMemo(() => getGrossAmount(expenses), [expenses])
  const reversedExpenseAmount = useMemo(() => getReversedAmount(expenses), [expenses])
  const expenseAmount = useMemo(() => getNetAmount(expenses), [expenses])
  const grossDepositAmount = useMemo(() => getGrossAmount(deposits), [deposits])
  const reversedDepositAmount = useMemo(() => getReversedAmount(deposits), [deposits])
  const depositAmount = useMemo(() => getNetAmount(deposits), [deposits])

  const expenseColumns = useMemo(() => [
    { header: "Type", cell: ({ row }) => row.original.expense_type?.name || row.original.expenseType?.name || "-" },
    { header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.expense_date ? formatReadableDate(row.original.expense_date) : "-" },
    {
      header: "Amount",
      cell: ({ row }) => (
        <div className="d-flex gap-1 align-items-center">
          <span className={`fw-semibold ${row.original.is_reversed ? "text-muted" : ""}`}>{formatCurrency(row.original.amount || 0, row.original.account?.currency || "BDT")}</span>
          <ReversalBadge isReversed={row.original.is_reversed} />
        </div>
      ),
    },
    { header: "Action", cell: ({ row }) => <Button size="sm" color="primary" onClick={() => { setSelectedExpenseViewId(row.original.id); setExpenseViewModal(true) }}>View</Button> },
  ], [])

  const depositColumns = useMemo(() => [
    { header: "From", cell: ({ row }) => row.original.from_account?.name || row.original.fromAccount?.name || "-" },
    { header: "To", cell: ({ row }) => row.original.to_account?.name || row.original.toAccount?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.transfer_date ? formatReadableDate(row.original.transfer_date) : "-" },
    {
      header: "Amount",
      cell: ({ row }) => (
        <div className="d-flex gap-1 align-items-center">
          <span className={`fw-semibold ${row.original.is_reversed ? "text-muted" : ""}`}>{formatCurrency(row.original.amount || 0, "BDT")}</span>
          <ReversalBadge isReversed={row.original.is_reversed} />
        </div>
      ),
    },
    { header: "Action", cell: ({ row }) => <Button size="sm" color="primary" onClick={() => { setSelectedDepositViewId(row.original.id); setDepositViewModal(true) }}>View</Button> },
  ], [])

  return (
    <div className="d-flex flex-column gap-4">
      <Row className="g-3">
        {permissionFlags.canViewAccounts && (
          <Col md={4}>
            <WorkspaceStatCard title="Accounts Visible" value={accountBalances.length} subtitle="Balance snapshot from dashboard" icon="mdi mdi-bank-outline" color="primary" />
          </Col>
        )}
        {permissionFlags.canViewExpenses && (
          <Col md={4}>
            <WorkspaceStatCard title="Gross Expenses" value={formatCurrency(grossExpenseAmount, "BDT")} subtitle="All visible expense records" icon="mdi mdi-receipt-text-outline" color="secondary" />
          </Col>
        )}
        {permissionFlags.canViewExpenses && (
          <Col md={4}>
            <WorkspaceStatCard title="Reversed Expenses" value={formatCurrency(reversedExpenseAmount, "BDT")} subtitle={`${reversedExpenses.length} reversed expense(s)`} icon="mdi mdi-undo-variant" color="info" />
          </Col>
        )}
        {permissionFlags.canViewExpenses && (
          <Col md={4}>
            <WorkspaceStatCard title="Net Expenses" value={formatCurrency(expenseAmount, "BDT")} subtitle="Current expense impact after reversals" icon="mdi mdi-receipt" color="danger" />
          </Col>
        )}
        {permissionFlags.canViewFundTransfers && (
          <Col md={4}>
            <WorkspaceStatCard title="Gross Deposits" value={formatCurrency(grossDepositAmount, "BDT")} subtitle="All visible deposit records" icon="mdi mdi-bank-transfer" color="secondary" />
          </Col>
        )}
        {permissionFlags.canViewFundTransfers && (
          <Col md={4}>
            <WorkspaceStatCard title="Reversed Deposits" value={formatCurrency(reversedDepositAmount, "BDT")} subtitle={`${reversedDeposits.length} reversed deposit(s)`} icon="mdi mdi-swap-horizontal" color="info" />
          </Col>
        )}
        {permissionFlags.canViewFundTransfers && (
          <Col md={4}>
            <WorkspaceStatCard title="Net Deposits" value={formatCurrency(depositAmount, "BDT")} subtitle="Current deposit impact after reversals" icon="mdi mdi-bank-transfer-in" color="success" />
          </Col>
        )}
      </Row>

      <Row className="g-3">
        {permissionFlags.canViewAccounts && (
          <Col xl={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Account Balances</h5>
                    <p className="text-muted mb-0 small">Fast access to current cash and bank positions.</p>
                  </div>
                  <Link to="/account-list" className="btn btn-sm btn-light border">Open accounts</Link>
                </div>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <tbody>
                      {accountBalances.slice(0, 8).map(item => (
                        <tr key={item.id}>
                          <td>
                            <div className="fw-semibold">{item.name}</div>
                            <div className="text-muted small">{item.account_type || "-"}</div>
                          </td>
                          <td className="text-end fw-semibold">{formatCurrency(item.balance || 0, item.currency || "BDT")}</td>
                        </tr>
                      ))}
                      {!accountBalances.length && <tr><td className="text-muted text-center py-4">No accounts found.</td></tr>}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        )}

        {permissionFlags.canViewExpenses && (
          <Col xl={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Expenses</h5>
                    <p className="text-muted mb-0 small">Recent expense activity from the same workspace.</p>
                  </div>
                  <div className="d-flex gap-2">
                    {permissionFlags.canAddExpense && <Button size="sm" color="primary" onClick={() => { setSelectedExpense(null); setExpenseModal(true) }}>Add</Button>}
                    <Link to="/account-expense-list" className="btn btn-sm btn-light border">Full page</Link>
                  </div>
                </div>
                <TableContainer columns={expenseColumns} data={expenses} loading={expenseLoading} isPagination={false} />
              </CardBody>
            </Card>
          </Col>
        )}

        {permissionFlags.canViewFundTransfers && (
          <Col xl={4}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">Fund Transfers</h5>
                    <p className="text-muted mb-0 small">Recent transfers between accounts.</p>
                  </div>
                  <div className="d-flex gap-2">
                    {permissionFlags.canAddFundTransfer && <Button size="sm" color="success" onClick={() => { setSelectedDeposit(null); setDepositModal(true) }}>Add</Button>}
                    <Link to="/account-fund-transfer-list" className="btn btn-sm btn-light border">Full page</Link>
                  </div>
                </div>
                <TableContainer columns={depositColumns} data={deposits} loading={depositLoading} isPagination={false} />
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex flex-wrap justify-content-between gap-2">
            <div>
              <h5 className="mb-1">Related Cash & Bank Tools</h5>
              <p className="text-muted mb-0 small">Keep less frequent finance activities reachable from the same place.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {permissionFlags.canViewVehiclePurchases && <Link to="/account-vehicle-purchase-list" className="btn btn-light border">Vehicle Purchases</Link>}
              {permissionFlags.canViewImporterPayments && <Link to="/account-importer-payment-list" className="btn btn-light border">Importer Payments</Link>}
            </div>
          </div>
        </CardBody>
      </Card>

      <AccountExpenseAddEdit modal={expenseModal} isEdit={!!selectedExpense?.id} editedData={selectedExpense} toggleModal={() => { setExpenseModal(prev => !prev); if (expenseModal) setSelectedExpense(null) }} />
      <AccountExpenseView modal={expenseViewModal} toggleModal={() => { setExpenseViewModal(prev => !prev); if (expenseViewModal) setSelectedExpenseViewId(null) }} expense={expenseViewResponse?.data} loading={expenseViewLoading} />
      <AccountFundTransferAddEdit modal={depositModal} isEdit={!!selectedDeposit?.id} editedData={selectedDeposit} toggleModal={() => { setDepositModal(prev => !prev); if (depositModal) setSelectedDeposit(null) }} />
      <AccountFundTransferView modal={depositViewModal} toggleModal={() => { setDepositViewModal(prev => !prev); if (depositViewModal) setSelectedDepositViewId(null) }} deposit={depositViewResponse?.data} loading={depositViewLoading} />
    </div>
  )
}

export default CashBankTab
